import { ArrowLeft, Compass, LockKeyhole, RefreshCw, ServerCrash, WifiOff } from "lucide-react";
import type { ReactNode } from "react";
import { isRouteErrorResponse, Link, useNavigate, useRouteError } from "react-router";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { ApiError } from "@/lib/api";

/**
 * Route-level error UI. Always shows the API `requestId` when there is one: it is
 * the only handle support has to find the request in the logs (principles §
 * observability), so it is copyable, not buried in the console.
 */

function ErrorFrame({
  icon,
  eyebrow,
  title,
  description,
  requestId,
  actions,
  detail,
}: {
  icon: ReactNode;
  eyebrow: string;
  title: string;
  description: ReactNode;
  requestId?: string | undefined;
  actions?: ReactNode;
  detail?: string | undefined;
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center bg-canvas-glow px-4 py-12">
      <div className="w-full max-w-lg text-center">
        <div className="border-brand-gradient mx-auto mb-6 flex size-14 items-center justify-center rounded-[16px] text-primary-ink shadow-sm [&_svg]:size-6">{icon}</div>
        <span className="eyebrow mb-4">{eyebrow}</span>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-fg-muted">{description}</p>

        {requestId && (
          <div className="mt-6 inline-flex items-center gap-2 rounded-pill border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-fg-muted shadow-xs">
            <span className="kicker text-fg-subtle">Reference</span>
            <code className="font-mono text-fg-soft">{requestId}</code>
            <CopyButton value={requestId} size="xs" toastMessage="Reference copied" />
          </div>
        )}

        {actions && <div className="mt-7 flex flex-wrap items-center justify-center gap-2">{actions}</div>}

        {detail && import.meta.env.DEV && (
          <pre className="mt-8 max-h-56 overflow-auto rounded-card border border-line bg-surface p-3 text-left text-xs text-fg-muted">{detail}</pre>
        )}
      </div>
      <Logo className="mt-12 h-6 opacity-60" title="lean2automate" />
    </main>
  );
}

/** Root `ErrorBoundary`: anything a route (or its lazy module) throws lands here. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const reload = () => window.location.reload();

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />;

  if (error instanceof ApiError) {
    if (error.status === 403) {
      return (
        <ErrorFrame
          icon={<LockKeyhole />}
          eyebrow="Not allowed"
          title="You don't have access to this"
          description="Your role doesn't include this area. An owner or admin can change that in Settings → Roles."
          requestId={error.requestId}
          actions={
            <Button asChild variant="secondary" leftIcon={<ArrowLeft />}>
              <Link to="/">Back to the dashboard</Link>
            </Button>
          }
        />
      );
    }
    if (error.code === "NETWORK_ERROR") {
      return (
        <ErrorFrame
          icon={<WifiOff />}
          eyebrow="Offline"
          title="We can't reach the server"
          description="Check your connection. Your unsaved work is still in this tab."
          actions={<Button leftIcon={<RefreshCw />} onClick={reload}>Try again</Button>}
        />
      );
    }
    return (
      <ErrorFrame
        icon={<ServerCrash />}
        eyebrow="Something broke"
        title="That request didn't go through"
        description={error.message}
        requestId={error.requestId}
        actions={
          <>
            <Button leftIcon={<RefreshCw />} onClick={reload}>
              Try again
            </Button>
            <Button asChild variant="secondary">
              <Link to="/">Go to the dashboard</Link>
            </Button>
          </>
        }
      />
    );
  }

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : "Unknown error";
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <ErrorFrame
      icon={<ServerCrash />}
      eyebrow="Something broke"
      title="This screen hit an unexpected error"
      description="The rest of the app is fine. Reload this page, and tell us the reference below if it keeps happening."
      actions={
        <>
          <Button leftIcon={<RefreshCw />} onClick={reload}>
            Reload
          </Button>
          <Button asChild variant="secondary">
            <Link to="/">Go to the dashboard</Link>
          </Button>
        </>
      }
      detail={stack ?? message}
    />
  );
}

export function NotFound() {
  const navigate = useNavigate();
  return (
    <ErrorFrame
      icon={<Compass />}
      eyebrow="404"
      title="That page doesn't exist"
      description="The link may be old, or the record may have been deleted. Press ⌘K to search for it."
      actions={
        <>
          <Button variant="secondary" leftIcon={<ArrowLeft />} onClick={() => navigate(-1)}>
            Go back
          </Button>
          <Button asChild>
            <Link to="/">Go to the dashboard</Link>
          </Button>
        </>
      }
    />
  );
}

/** Shown by <RequirePermission> when the session lacks a permission. */
export function Forbidden({ what = "this area" }: { what?: string }) {
  return (
    <ErrorFrame
      icon={<LockKeyhole />}
      eyebrow="Not allowed"
      title={`You don't have access to ${what}`}
      description="Your role doesn't include it. An owner or admin can grant it in Settings → Roles."
      actions={
        <Button asChild variant="secondary" leftIcon={<ArrowLeft />}>
          <Link to="/">Back to the dashboard</Link>
        </Button>
      }
    />
  );
}
